/**
 * The sizes of the app's own chrome.
 *
 * Here rather than at the top of `AppLayout` because a screen that manages its
 * own scrolling has to know them too, and the two copies drifted the moment the
 * tab bar started floating: the bar grew a 14px margin on each side, `ChatPage`
 * went on subtracting the old 56, and the composer slid under it by exactly the
 * difference. One definition, imported by both, cannot do that again.
 */

/** The tab bar itself, before its margins or the home-indicator inset. */
export const TAB_BAR_HEIGHT = 56;

/** The gap the floating bar keeps from the screen edge, top and bottom. */
export const TAB_BAR_MARGIN = 14;

/** Everything the fixed bar occupies, ignoring the safe-area inset below it. */
export const TAB_BAR_SPACE = TAB_BAR_HEIGHT + TAB_BAR_MARGIN * 2;

/** The sticky header: a 44px row of controls with 8px of padding either side. */
export const HEADER_HEIGHT = 60;

/** The air between a screen's last row and whatever is under it. */
export const BOTTOM_BREATH = 8;

/**
 * Everything the page keeps free below a screen: the home indicator, the
 * floating bar standing on it, and a little air above that.
 *
 * One expression, because it was two — the variable below carried the bar and
 * the inset, and `<main>` added the inset a second time. On a desktop the
 * inset is nought and the mistake was invisible; on a phone with a gesture bar
 * it opened a finger-wide band of nothing between the composer and the
 * navigation, and the band stayed when the bar went away, because that half of
 * the sum was never the bar's to remove.
 */
export const BOTTOM_RESERVE =
  `calc(var(--safe-bottom) + ${TAB_BAR_SPACE}px + ${BOTTOM_BREATH}px)` as const;

/**
 * The same, while somebody is typing: nothing at all but the air.
 *
 * The bar is hidden and the keyboard is standing where the home indicator was,
 * so neither is the page's to give up.
 */
export const BOTTOM_RESERVE_TYPING = `${BOTTOM_BREATH}px` as const;

/**
 * How much of the bottom the app's own chrome is using — and it is not constant.
 *
 * When a keyboard comes up the tab bar goes away (see `useKeyboardInset`), and a
 * pane that went on subtracting it would leave a bar-shaped hole above the
 * composer, on the one screen where every line of the conversation counts. A
 * variable rather than a prop because `PANE_HEIGHT` is a string in a `calc`,
 * read by four screens that know nothing about each other.
 */
export const CHROME_BOTTOM = "--chrome-bottom";

/**
 * How much of the top the notice rail is using, in pixels.
 *
 * Same argument as `CHROME_BOTTOM`, at the other edge. The rail is `fixed`, so
 * it took no space and sat squarely on whatever the page put first — which is
 * its heading, on every screen. Reserving the space it actually occupies is the
 * only honest fix: the notice stays put while the page scrolls under it, and
 * the page still starts below it.
 *
 * Nought whenever there is nothing to say, which is almost always.
 */
export const CHROME_TOP = "--chrome-top";

/**
 * How much of the viewport the keyboard is covering, in pixels.
 *
 * `dvh` does not answer this. It follows the browser's own furniture — an
 * address bar sliding away — and on iOS the layout viewport does not move for
 * a keyboard at all: only the *visual* viewport shrinks, which is why this has
 * to be measured rather than expressed in CSS.
 */
export const KEYBOARD_INSET = "--keyboard-inset";

/**
 * How tall the part of the window a person can actually see is, in pixels.
 *
 * Published, and deliberately **not** used to size a pane — that was tried and
 * it was wrong. A pane is laid out in the *layout* viewport, and this measures
 * the *visual* one; on an iPhone the two come apart exactly when a keyboard is
 * up, because the page keeps its height and the browser scrolls it instead. A
 * pane sized to what is visible therefore ended half a screen above where the
 * window had been scrolled to, taking the composer with it.
 *
 * Kept because it is the honest measurement and anything anchored to the
 * visible area rather than laid out in the page can use it.
 */
export const VIEWPORT_HEIGHT = "--viewport-height";

/** What a screen has left, once the header, the bar, the insets and any
    keyboard are gone. */
/**
 * What a screen has left, once the header, the bar, the insets and any
 * keyboard are gone.
 *
 * `100dvh` and not the measured visible height, for the reason written on
 * `VIEWPORT_HEIGHT`: this height is laid out in the page, and the page is what
 * `dvh` describes.
 *
 * Nothing here names the home indicator or the bar: both are inside
 * `--chrome-bottom`, which is the single answer to «how much of the bottom is
 * spoken for», and which goes down to a few pixels of air while somebody is
 * typing.
 */
export const PANE_HEIGHT =
  `calc(100dvh - var(--safe-top) - ${HEADER_HEIGHT}px - var(${CHROME_TOP}, 0px) - var(${CHROME_BOTTOM}, ${BOTTOM_RESERVE}) - var(${KEYBOARD_INSET}, 0px))` as const;
